/**
 * @module features/devices/components/TelemetryTrendCharts
 * @description Interactive SVG area/line charts (CPU %, Memory %, WiFi RSSI) over the
 * retained logs (<= 30), oldest on the left. Hover/focus shows the exact value and
 * its timestamp. Pure SVG: no chart dependency, 60fps, zero layout shift.
 */
import * as React from "react";
import { cn } from "@/lib/utils";
import { DisplayModuleTelemetryLog } from "../types/telemetry";
import { formatDateTimeSeconds } from "../utils/format";
import { CHART_CARD_HEIGHT } from "./TelemetryDashboardSkeleton";

/** Extract a numeric dBm from strings like "-58 dBm" / "-58"; NaN when unparsable. */
export function parseRssi(raw: string): number {
  const m = raw.match(/-?\d+(\.\d+)?/);
  return m ? Number(m[0]) : NaN;
}

interface Point {
  x: number;
  y: number;
  value: number;
  ts: string;
}

const W = 300;
const H = 110;
const PAD = 6;

export function buildPoints(values: Array<{ v: number; ts: string }>, min: number, max: number): Point[] {
  const span = max - min || 1;
  const n = values.length;
  return values.map(({ v, ts }, i) => ({
    x: n === 1 ? W / 2 : PAD + (i * (W - PAD * 2)) / (n - 1),
    y: H - PAD - ((Math.min(max, Math.max(min, v)) - min) / span) * (H - PAD * 2),
    value: v,
    ts,
  }));
}

interface ChartProps {
  title: string;
  unit: string;
  values: Array<{ v: number; ts: string }>;
  min: number;
  max: number;
  colorClass: string;
}

function TrendChart({ title, unit, values, min, max, colorClass }: ChartProps) {
  const [active, setActive] = React.useState<number | null>(null);
  const points = React.useMemo(() => buildPoints(values, min, max), [values, min, max]);
  const line = points.map((p) => `${p.x},${p.y}`).join(" ");
  const area = points.length ? `${PAD},${H - PAD} ${line} ${points[points.length - 1].x},${H - PAD}` : "";
  const shown = active !== null ? points[active] : points[points.length - 1];

  return (
    <section
      aria-label={`${title} trend`}
      className={cn("rounded-xl border border-border/80 bg-card p-4 shadow-xs flex flex-col", CHART_CARD_HEIGHT)}
    >
      <div className="flex items-baseline justify-between gap-2">
        <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{title}</h3>
        {shown && (
          <p className="text-[11px] text-muted-foreground text-right">
            <span className="font-semibold text-foreground">
              {shown.value.toFixed(1)}
              {unit}
            </span>{" "}
            · {formatDateTimeSeconds(shown.ts)}
          </p>
        )}
      </div>
      {points.length === 0 ? (
        <p className="flex-1 grid place-items-center text-xs text-muted-foreground">No data points yet</p>
      ) : (
        <svg
          viewBox={`0 0 ${W} ${H}`}
          className={cn("mt-2 w-full flex-1 min-h-0", colorClass)}
          preserveAspectRatio="none"
          role="img"
          aria-label={`${title}, ${points.length} samples`}
        >
          <polygon points={area} className="fill-current opacity-10" />
          <polyline points={line} fill="none" strokeWidth="1.8" className="stroke-current" vectorEffect="non-scaling-stroke" />
          {points.map((p, i) => (
            <circle
              key={`${p.ts}-${i}`}
              cx={p.x}
              cy={p.y}
              r={active === i ? 4 : 2.2}
              tabIndex={0}
              aria-label={`${p.value.toFixed(1)}${unit} at ${formatDateTimeSeconds(p.ts)}`}
              className="fill-current outline-none"
              onMouseEnter={() => setActive(i)}
              onMouseLeave={() => setActive(null)}
              onFocus={() => setActive(i)}
              onBlur={() => setActive(null)}
            />
          ))}
        </svg>
      )}
    </section>
  );
}

interface TelemetryTrendChartsProps {
  /** Newest first (as delivered by the API); charts render oldest to newest. */
  logs: DisplayModuleTelemetryLog[];
}

export function TelemetryTrendCharts({ logs }: TelemetryTrendChartsProps) {
  const ordered = React.useMemo(() => [...logs].reverse(), [logs]);
  const cpu = ordered.map((l) => ({ v: l.cpu_load, ts: l.received_timestamp }));
  const mem = ordered.map((l) => ({ v: l.memory_load_percentage, ts: l.received_timestamp }));
  const rssi = ordered
    .map((l) => ({ v: parseRssi(l.wifi_rssi), ts: l.received_timestamp }))
    .filter((p) => Number.isFinite(p.v));

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
      <TrendChart title="CPU load" unit="%" values={cpu} min={0} max={100} colorClass="text-sky-500" />
      <TrendChart title="Memory load" unit="%" values={mem} min={0} max={100} colorClass="text-violet-500" />
      <div className="lg:col-span-2">
        <TrendChart title="WiFi RSSI" unit=" dBm" values={rssi} min={-100} max={-30} colorClass="text-emerald-500" />
      </div>
    </div>
  );
}

